import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { captionPlan, revealEnd } from "./arcs";
import { revealBox } from "./StrokeReveal";
import { imageSrc } from "../../lib/api";
import { audio } from "../../lib/audio";
import { LID, LOOK, slow, WARM } from "../bench/Blink";

const STREAKS = [
  [6, "#FF2E9E"], [13, "#FFBD38"], [21, "#26E5FF"], [29, "#FF2E9E"], [37, "#FFBD38"], [46, "#26E5FF"],
  [54, "#FF2E9E"], [62, "#26E5FF"], [70, "#FFBD38"], [79, "#FF2E9E"], [87, "#26E5FF"], [94, "#FFBD38"],
];

// The arc's own clock in seconds: the 3D room's (which can pause at a hold) or wall time for the calm telling.
// One shared clock for the calm telling, so scenes, hints, captions and her line stay in step (a tap can move it on).
export const calmClock = {
  t0: performance.now(),
  off: 0,
  reset() {
    this.t0 = performance.now();
    this.off = 0;
  },
  now() {
    return (performance.now() - this.t0) / 1000 + this.off;
  },
  skipTo(t) {
    this.off += Math.max(0, t - this.now());
  },
};
export function useArcTime(clock, calm) {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT(calm ? calmClock.now() : clock.current?.t ?? 0), 200);
    return () => clearInterval(id);
  }, [calm, clock]);
  return t;
}
// Once her painting has emerged: where it sits, so her line can go below it and captions above it.
function useEmerged(calm, t, plan) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    if (calm) return;
    const on = () => setBox(window.__afaeRevealBox || null);
    window.addEventListener("afae-reveal-box", on);
    window.addEventListener("resize", on);
    return () => (window.removeEventListener("afae-reveal-box", on), window.removeEventListener("resize", on));
  }, [calm]);
  if (calm) return plan && t >= plan.fade[0] ? revealBox() : null;
  return window.__afaeReveal?.started ? box || window.__afaeRevealBox || revealBox() : null;
}

function StreetStill() {
  return (
    <>
      <div className="absolute inset-x-0 top-0 h-[52%] bg-[linear-gradient(#0C0918,#140E24_70%,#1E1430)]">
        {STREAKS.map(([x, c], i) => (
          <span key={x} className="absolute bottom-[8%] w-[3px] rounded-sm" style={{ left: `${x}%`, height: `${28 + ((i * 37) % 30)}%`, background: c, boxShadow: `0 0 14px 2px ${c}66` }} />
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-0 h-[48%] bg-[linear-gradient(#0A0A12,#05060A)]">
        {STREAKS.map(([x, c], i) => (
          <span key={x} className="absolute top-0 w-[4px] opacity-70 blur-[2px]" style={{ left: `${x}%`, height: `${55 + ((i * 23) % 40)}%`, background: `linear-gradient(${c}, transparent)` }} />
        ))}
      </div>
    </>
  );
}

const BROODING = ["braids", "bun", "curls", "headscarf", "locs", "plait"];
function ProfilesStill() {
  return (
    <div className="absolute inset-0 grid grid-cols-3 place-items-center gap-2 bg-[#F6F0E8] p-6 sm:grid-cols-6">
      {BROODING.map((n) => (
        <img key={n} src={`${process.env.PUBLIC_URL || ""}/arcs/brooding/${n}.webp`} alt="" className="w-full max-w-[220px] opacity-80" />
      ))}
    </div>
  );
}

function AnganStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#C9A27A,#A87D58_62%,#5E6B34)]">
      <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent_30%,rgba(255,214,150,0.35)_42%,transparent_55%)]" />
      <img src={`${process.env.PUBLIC_URL || ""}/arcs/angan/pots.webp`} alt="" className="absolute bottom-[12%] left-1/2 w-[86vw] max-w-[560px] -translate-x-1/2" />
    </div>
  );
}

function CanvasStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#F7C6C9,#EFA3B4_55%,#8E5F55_56%,#6E4A36)]">
      <div className="absolute left-1/2 top-[56%] h-[22vh] w-[70vw] max-w-[520px] -translate-x-1/2 -translate-y-1/2 bg-[#FBF8F3] shadow-[0_20px_40px_-20px_rgba(60,20,30,0.6)]" />
    </div>
  );
}

function HomeStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#6E6585,#3D3850_58%,#1A1A22_59%,#121218)]">
      <div className="absolute bottom-[41%] left-1/2 h-[24vh] w-[46vw] max-w-[300px] -translate-x-1/2 bg-[#E6E3D8]">
        <div className="absolute left-[56%] top-[28%] h-[30%] w-[26%] bg-[#FFD27A] shadow-[0_0_30px_6px_rgba(255,200,110,0.55)]" />
        <div className="absolute bottom-0 left-[14%] h-[42%] w-[18%] bg-[#B6231F]" />
      </div>
      <div className="absolute inset-x-0 bottom-0 h-[41%] bg-[linear-gradient(rgba(255,190,100,0.28),transparent_60%)]" />
    </div>
  );
}

function TableStill() {
  return <div className="absolute inset-0 bg-[#8B6A4E] bg-cover bg-center" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/arcs/existence/table.webp)` }} />;
}

function PondStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#211C46,#7A2F5E_38%,#F0874A_52%,#5B2A4A_53%,#1A1630)]">
      <div className="absolute inset-y-0 left-0 w-[14%] bg-[#0E0B12]/85" />
      <div className="absolute inset-y-0 right-[6%] w-[9%] bg-[#0E0B12]/80" />
    </div>
  );
}

function WindowStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#F4DDB0,#E9C894)]">
      <div className="absolute left-1/2 top-[22%] h-[36vh] w-[30vh] max-w-[60vw] -translate-x-1/2 border-[10px] border-[#E8E2D4] bg-[linear-gradient(#1D2E57,#33486F)] shadow-[0_18px_30px_-18px_rgba(60,40,20,0.5)]" />
      <div className="absolute left-[18%] top-[8%] h-[30vh] w-[46vw] rounded-[45%] bg-[#C21B78]/80 blur-[6px]" />
    </div>
  );
}

function WinterStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#0E1030,#2A2558_45%,#9C5A7E_72%,#C9C9DE_73%,#8E92B4)]">
      <div className="absolute right-[22%] top-[16%] h-16 w-16 rounded-full bg-[#FFF3BC] shadow-[0_0_60px_18px_rgba(255,240,180,0.35)]" />
      {[8, 24, 42, 63, 81].map((x, i) => (
        <div key={x} className="absolute bottom-[27%] w-[16vw] bg-[#0F2423]" style={{ left: `${x - 8}%`, height: `${38 + (i % 3) * 9}%`, clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }} />
      ))}
    </div>
  );
}

function SunsetStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#A4243E,#E5727E_34%,#F8A55E_49%,#C46A6E_51%,#8E2A42)]">
      <div className="absolute left-1/2 top-[44%] h-14 w-14 -translate-x-1/2 rounded-full bg-[#FFD08A] shadow-[0_0_50px_16px_rgba(255,190,120,0.45)]" />
    </div>
  );
}

const TULIP_STILL = ["#7A1830", "#F2772A", "#F8CC3E", "#4B6AD2", "#F2772A", "#7A1830", "#F8CC3E"];
function TulipsStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#FBE3E1,#E9A3A6)]">
      {TULIP_STILL.map((c, i) => (
        <div key={i} className="absolute bottom-[24%]" style={{ left: `${30 + i * 6.5}%`, height: `${30 + ((i * 13) % 12)}%` }}>
          <div className="absolute bottom-0 left-1/2 h-full w-[3px] -translate-x-1/2 bg-[#3E7A33]" />
          <div className="absolute left-1/2 top-0 h-9 w-7 -translate-x-1/2 rounded-b-full rounded-t-[40%]" style={{ background: c }} />
        </div>
      ))}
    </div>
  );
}

function OrchardStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#EAAF76,#F4C98E_45%,#445439_70%,#2F3B26)]">
      {[6, 26, 52, 74].map((x) => (
        <div key={x} className="absolute top-[12%] h-[38%] w-[26vw] rounded-[45%] bg-[#4A2F52]/85" style={{ left: `${x - 6}%` }} />
      ))}
    </div>
  );
}

function GardenStill() {
  return <div className="absolute inset-0 bg-[linear-gradient(#CFE6F7,#9CCB7A_48%,#4E8F3A_49%,#3B6E2C)]" />;
}

function StorybookStill() {
  return (
    <div className="absolute inset-0 bg-[#FAF5EA]">
      <img src={`${process.env.PUBLIC_URL || ""}/arcs/butterfly/girl.webp`} alt="" className="absolute left-1/2 top-[34%] w-[40vw] max-w-[260px] -translate-x-1/2" />
    </div>
  );
}

function PlanetStill() {
  return (
    <div className="absolute inset-0 bg-[#05070F]">
      <div className="absolute left-1/2 top-[38%] h-[42vmin] w-[42vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_35%_35%,#3C8A5E,#1D5F47_45%,#123A5E_75%,#0A1A33)] shadow-[0_0_60px_10px_rgba(90,150,255,0.25)]" />
    </div>
  );
}

function TempleStill() {
  return (
    <div className="absolute inset-0 bg-[#1B2A63]">
      <img src={`${process.env.PUBLIC_URL || ""}/arcs/home/temple.webp`} alt="" className="absolute inset-x-0 bottom-0 mx-auto h-[88%] object-contain opacity-90" />
    </div>
  );
}

function ShivaStill() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#5A7BD0,#22356F_55%,#141C3A)]">
      <img src={`${process.env.PUBLIC_URL || ""}/arcs/shiva/shiva.webp`} alt="" className="absolute left-1/2 top-[44%] w-[62vmin] -translate-x-1/2 -translate-y-1/2" />
    </div>
  );
}

function FaithStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#14172A,#2E3346_70%,#0B0C12)]">
      <div className="absolute left-1/2 top-[30%] h-[34vmin] w-[34vmin] -translate-x-1/2 rounded-full bg-[#F5EFE2] shadow-[0_0_80px_20px_rgba(245,235,215,0.25)]" />
      <div className="absolute left-[18%] top-[18%] h-[22vmin] w-[40vmin] rounded-[45%] bg-[#E9A9B8]/80 blur-[4px]" />
    </div>
  );
}

function GangesStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#141A4A,#5A4A86_40%,#F2A27A_58%,#2B4C8E_59%,#1B2A5A_78%,#3A2C2E_79%)]">
      <div className="absolute bottom-[21%] left-1/2 h-[46vh] w-[36vh] max-w-[70vw] -translate-x-1/2 rounded-t-full border-[14px] border-b-0 border-[#F29A1E]" />
      <div className="absolute bottom-[21%] left-1/2 h-[14vh] w-[7vh] -translate-x-1/2 bg-[linear-gradient(#FFE27A,#FF9A2E_60%,#1A1210)] [clip-path:polygon(50%_0,85%_60%,70%_100%,30%_100%,15%_60%)]" />
    </div>
  );
}

function SoulStill() {
  return (
    <div className="absolute inset-0 bg-[#2A211E]">
      <div className="absolute left-1/2 top-[18%] h-[52vh] w-[42vh] max-w-[80vw] -translate-x-1/2 overflow-hidden border-[10px] border-[#6B4A32] bg-[linear-gradient(#1A1F5A,#4B3C8E_55%,#2F5A8E_56%,#14204A)]">
        <div className="absolute left-[22%] top-[22%] h-[16%] w-[26%] rounded-full bg-[#FFC63A] shadow-[0_0_40px_10px_rgba(255,190,60,0.5)]" />
        <div className="absolute right-[22%] top-[24%] h-[13%] w-[21%] rounded-full bg-[#EDEBE2]" />
      </div>
    </div>
  );
}

function FeatherStill() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,#FFF8F0,#F6E6E4_60%,#E8D6DA)]">
      <div className="absolute left-1/2 top-[36%] h-[30vmin] w-[22vmin] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-[radial-gradient(ellipse,#0B1E66_30%,#0E7C8C_42%,#F2C53D_55%,#8A6A2A_64%,#1E8A5A_75%,transparent_77%)]" />
      <div className="absolute left-1/2 top-[51%] h-[34vh] w-[3px] -translate-x-1/2 bg-[#C7B48A]" />
    </div>
  );
}

function LoveStill() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,#D6D9DE_9%,#5E6878_10%,#2A3040_70%)]">
      <div className="absolute left-[18%] right-[20%] top-[47%] h-[6px] -rotate-3 rounded-full bg-[#14141A]" />
      <div className="absolute left-[38%] top-[40%] h-[7vh] w-[6vh] rounded-[50%] bg-[#101014]" />
      <div className="absolute left-[52%] top-[40.5%] h-[7vh] w-[6vh] rounded-[50%] bg-[#101014]" />
    </div>
  );
}

function DestinyStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#0B1230,#16204A_55%,#0A1028_56%,#060A18)]">
      <div className="absolute left-1/2 top-[16%] h-[34vmin] w-[34vmin] -translate-x-1/2 rounded-full bg-[#EEF0F8] shadow-[0_0_90px_24px_rgba(200,210,255,0.3)]" />
      {["#E23A44", "#F58DB4", "#7393F5", "#F4F4FA", "#E23A44", "#F58DB4"].map((c, i) => (
        <div key={i} className="absolute bottom-[44%] w-[10vw] opacity-90" style={{ left: `${12 + i * 13}%`, height: `${22 + ((i * 7) % 10)}%`, background: c, clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }} />
      ))}
    </div>
  );
}

function BouquetStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#F4E2BC,#E2C79A_62%,#8A5E3C_63%,#6E4A30)]">
      <div className="absolute bottom-[37%] left-1/2 h-[46vh] w-[30vh] max-w-[60vw] -translate-x-1/2 border-[10px] border-[#FBF8F2] bg-[linear-gradient(#A8D4F5,#E9F4FF_60%,#4E8A3A_61%)]" />
      <div className="absolute bottom-[37%] left-1/2 h-[9vh] w-[6vh] -translate-x-1/2 rounded-b-[40%] bg-[#4E6FB8]" />
    </div>
  );
}

function DreamStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#5A4A78,#F2A6A0_55%,#FBD9A8_70%,#3A2A26_71%)]">
      {[6, 18, 30, 66, 78, 90].map((x) => (
        <div key={x} className="absolute bottom-0 top-0 w-[4vw] bg-[#22161A]/85" style={{ left: `${x}%` }} />
      ))}
      <div className="absolute bottom-[31%] left-1/2 h-[7vh] w-[10vh] -translate-x-1/2 bg-[#5A3A34] shadow-[0_0_50px_14px_rgba(255,200,110,0.45)]">
        <div className="absolute left-[18%] top-[30%] h-[30%] w-[22%] bg-[#FFD27A]" />
        <div className="absolute right-[18%] top-[30%] h-[30%] w-[22%] bg-[#FFD27A]" />
      </div>
    </div>
  );
}

function PromiseStill() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,#1C2A5E,#0B1236_70%)]">
      {[[30, 34], [44, 26], [58, 32], [38, 44], [64, 42]].map(([x, y], i) => (
        <div key={i} className="absolute h-[9vmin] w-[9vmin] rounded-[45%] bg-[#D6E4FA]/85 blur-[1px]" style={{ left: `${x}%`, top: `${y}%`, clipPath: "polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%)" }} />
      ))}
    </div>
  );
}

function ChhathStill() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(#C8241C,#F27A1E_35%,#FFD34A_52%,#2A1018_53%,#2A1018_58%,#B0508A_59%,#5A2A5E)]">
      <div className="absolute left-1/2 top-[38%] h-[16vmin] w-[16vmin] -translate-x-1/2 rounded-full bg-[#FFF0A8] shadow-[0_0_80px_30px_rgba(255,200,80,0.55)]" />
    </div>
  );
}

function SimmerStill() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,#FFE08A_4%,#FF8A1E_14%,#B4100E_34%,#3A0408_70%)]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,transparent_30%,rgba(214,162,60,0.35)_55%,transparent_75%)]" />
    </div>
  );
}

const BACKDROPS = { simmer: SimmerStill, chhath: ChhathStill, promise: PromiseStill, dream: DreamStill, bouquet: BouquetStill, destiny: DestinyStill, love: LoveStill, ganges: GangesStill, soul: SoulStill, feather: FeatherStill, faith: FaithStill, shiva: ShivaStill, temple: TempleStill, orchard: OrchardStill, garden: GardenStill, storybook: StorybookStill, planet: PlanetStill, tulips: TulipsStill, sunset: SunsetStill, winter: WinterStill, pond: PondStill, window: WindowStill, street: StreetStill, profiles: ProfilesStill, angan: AnganStill, canvas: CanvasStill, home: HomeStill, table: TableStill };

// The calm telling: every stage in order as a slowly drifting painted scene, a smooth cross-fade into her painting, her line, then the button.
export function CalmArc({ painting, arc, plan, onReady }) {
  const t = useArcTime(null, true);
  const i = Math.min(plan.scenes.length - 1, Math.floor(t / plan.per));
  const into = t >= plan.fade[0];
  const box = revealBox();
  // a tap moves on to the next scene
  const next = () => !into && calmClock.skipTo((i + 1) * plan.per);
  useEffect(() => {
    onReady?.();
    if (arc.ambient) audio.setScene(arc.ambient.scene);
    window.__afaeArc = { calm: true, slug: arc.slug };
    return () => delete window.__afaeArc;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [f0, f1] = plan.fade;
  const k = Math.min(1, Math.max(0, (t - f0) / (f1 - f0)));
  useEffect(() => {
    if (k >= 1 && arc.ambient) audio.setScene("reveal");
  }, [k >= 1]); // eslint-disable-line react-hooks/exhaustive-deps
  const Back = BACKDROPS[arc.calm.backdrop];
  return (
    <div data-testid="immersion-calm-arc" data-fade={k.toFixed(2)} data-scene={into ? "painting" : i} onPointerDown={next} className="absolute inset-0 overflow-clip bg-[#0B0A12]">
      {plan.frames ? (
        plan.scenes.map((sc, j) => (
          <img key={j} src={`${process.env.PUBLIC_URL || ""}${sc.src || `/arcs/calm/${arc.slug}-${j}.webp`}`} alt="" draggable={false} className={`absolute inset-0 h-full w-full object-cover ${j === i ? "afae-kb" : ""}`} style={{ "--kb": `${plan.per + 2}s`, opacity: j === i && !into ? 1 : j === i ? 0.25 : 0, transition: "opacity 1.8s ease-in-out" }} />
        ))
      ) : (
        <div key={i} className="afae-kb absolute inset-0" style={{ "--kb": `${plan.per + 2}s` }}>
          {Back && <Back />}
        </div>
      )}
      <div className="absolute inset-0 bg-[#0B0A12]" style={{ opacity: into ? 0.92 : 0, transition: "opacity 4s ease-in-out" }} />
      <img src={imageSrc(painting.image_url)} alt={painting.title} draggable={false} className="absolute object-contain" style={{ top: box.top, left: box.left, width: box.right - box.left, height: box.bottom - box.top, opacity: into ? 1 : 0, transform: into ? "scale(1)" : "scale(1.03)", transition: "opacity 4.5s ease-in-out, transform 6s ease-out" }} />
    </div>
  );
}

export function ArcLine({ arc, calm, clock, span }) {
  const t = useArcTime(clock, calm);
  const [a, b] = calm ? span || arc.calm.line : [arc.line.from, 1e9];
  const em0 = useEmerged(calm, t, calm ? { fade: [a - 2] } : null);
  // before the emergence the line passes once (about 6s, alone on screen); after it, it rests below the painting
  const on = t >= a && t < b && (em0 || t < a + 6);
  const low = (calm ? arc.calm.pos : arc.line.pos) === "low";
  const em = em0;
  // after the emergence her line sits below the painting, never across it
  if (em)
    return (
      <motion.p data-testid="arc-line" data-on={on ? "1" : "0"} data-below="1" initial={{ opacity: 0 }} animate={{ opacity: on ? 1 : 0 }} transition={{ duration: 1.6, ease: "easeOut" }} style={{ top: em.bottom + 10 }} className="pointer-events-none absolute inset-x-4 mx-auto max-w-2xl px-4 py-1 text-center font-display text-base italic leading-snug text-[#FFF4DC]/90 [text-shadow:0_1px_10px_rgba(5,6,12,0.9)] sm:text-xl [@media(max-height:500px)]:text-sm">
        {arc.line.text}
        {arc.line.cite && <span data-testid="arc-line-cite" className="mt-0.5 block font-display text-xs not-italic tracking-wide text-[#F6E7C8]/75 sm:text-sm">{arc.line.cite}</span>}
      </motion.p>
    );
  return (
    <motion.p
      data-testid="arc-line"
      data-on={on ? "1" : "0"}
      initial={{ opacity: 0 }}
      animate={{ opacity: on ? 1 : 0, y: on ? 0 : 6 }}
      transition={{ duration: 1.6, ease: "easeOut" }}
      className={`pointer-events-none absolute inset-x-6 mx-auto max-w-lg rounded-2xl bg-[#22152F]/[0.45] px-4 py-1.5 text-center font-display text-lg italic leading-snug text-[#FFF4DC]/90 backdrop-blur-[2px] sm:text-xl ${low ? "bottom-[9rem] sm:bottom-24" : "bottom-[9rem] sm:bottom-24"}`}
    >
      {arc.line.text}
      {arc.line.cite && <span className="mt-0.5 block font-display text-xs not-italic tracking-wide text-[#F6E7C8]/75">{arc.line.cite}</span>}
    </motion.p>
  );
}

// Thought fragments drifting in slow circles around the centre of the view.
export function ArcWords({ arc, clock }) {
  const t = useArcTime(clock, false);
  const on = t >= arc.words.from && t < arc.words.to;
  const play = arc.words.stillAt != null && t >= arc.words.stillAt ? "paused" : "running";
  if (arc.words.still)
    return (
      <motion.p data-testid="arc-words" data-on={on ? "1" : "0"} animate={{ opacity: on ? 1 : 0, y: on ? 0 : 8 }} transition={{ duration: 2.2 }} className="pointer-events-none absolute inset-x-0 top-[18%] text-center font-display text-xl italic text-[#FFF3D6]/90 [text-shadow:0_2px_16px_rgba(40,20,10,0.7)] sm:text-2xl">
        {arc.words.list[0]}
      </motion.p>
    );
  return (
    <motion.div data-testid="arc-words" data-on={on ? "1" : "0"} animate={{ opacity: on ? 1 : 0 }} transition={{ duration: 2.4 }} className="pointer-events-none absolute inset-0 overflow-clip">
      {arc.words.list.map((w, i) => {
        const r = 22 + ((i * 13) % 22);
        const dur = 34 + ((i * 7) % 18);
        return (
          <div key={i} className="afae-orbit absolute left-1/2 top-[46%] h-0 w-0" style={{ animationDuration: `${dur}s`, animationDelay: `-${(i * dur) / arc.words.list.length}s`, animationDirection: i % 2 ? "reverse" : "normal", animationPlayState: play }}>
            <span className="afae-orbit absolute whitespace-nowrap font-display text-sm italic text-white/45 [text-shadow:0_0_12px_rgba(20,10,40,0.9)] sm:text-lg" style={{ left: `${r}vmin`, animationDuration: `${dur}s`, animationDelay: `-${(i * dur) / arc.words.list.length}s`, animationDirection: i % 2 ? "normal" : "reverse", animationPlayState: play, transform: "translate(-50%,-50%)" }}>
              {w}
            </span>
          </div>
        );
      })}
    </motion.div>
  );
}

// Her story, a phrase at a time near the top of the room: small, calm, never catching a tap.
export function ArcCaptions({ arc, about, calm, clock }) {
  const t = useArcTime(clock, calm);
  const plan = useMemo(() => captionPlan(arc, about, calm), [arc, about, calm]);
  const cur = plan.findIndex((c) => t >= c.from && t < c.to);
  const em = useEmerged(calm, t, calm ? { fade: [Infinity] } : null);
  // once her painting is up, captions move above it (on very short screens they rest)
  // once her painting is up, no stage text remains beside it (only her line below)
  const emStyle = em ? { display: "none" } : undefined;
  // a soft lower third, clear of the scene's focal subject and above her line
  return (
    <div data-testid="arc-captions" data-index={cur} style={emStyle} className={`pointer-events-none absolute inset-x-4 mx-auto flex max-w-md justify-center ${arc.capLow ? "bottom-[6.6rem] sm:bottom-[2.2rem]" : "bottom-[16.5rem] sm:bottom-[12.5rem] [@media(max-height:500px)]:bottom-[8.5rem]"}`}>
      <AnimatePresence mode="wait">
        {cur >= 0 && (
          <motion.p
            key={cur}
            data-testid="arc-caption"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="rounded-lg bg-[#1B1426]/[0.38] px-3 py-1 text-center font-display text-[12.5px] leading-snug text-[#FFF4DC]/90 backdrop-blur-[2px] sm:text-sm"
          >
            {plan[cur].text}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

// A first-person blink at the given moments: the Memory Wall's eyelids, warm and faintly see-through, read from the arc's clock every frame.
// the pond's blink runs on the story clock itself: ease-in close, a short hold, ease-out open, drawn straight to the lids each frame
const lidAt = (d, k) => {
  const c = WARM.close * k, h = WARM.hold * k, o = WARM.open * k;
  if (d < 0 || d >= c + h + o) return 0;
  if (d < c) return (d / c) ** 2;
  if (d < c + h) return 1;
  return (1 - (d - c - h) / o) ** 2;
};
export function ArcLids({ arc, clock }) {
  const root = useRef(null);
  const top = useRef(null);
  const bottom = useRef(null);
  const veil = useRef(null);
  useEffect(() => {
    let raf;
    let count = 0;
    let was = false;
    const tick = () => {
      const t = clock.current?.t ?? 0;
      const k = slow();
      const v = Math.max(0, ...arc.lids.map((b) => lidAt(t - b, k)));
      const shut = v >= 0.95;
      if (shut && !was) count += 1;
      was = shut;
      top.current.style.transform = bottom.current.style.transform = `scaleY(${v})`;
      veil.current.style.opacity = v;
      root.current.dataset.lid = v.toFixed(2);
      root.current.dataset.closed = shut ? "true" : "false";
      root.current.dataset.blinks = count;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [arc, clock]);
  return (
    <div ref={root} data-testid="arc-lids" data-closed="false" data-blinks="0" data-lid="0.00" className="pointer-events-none absolute inset-0 z-[2] overflow-hidden">
      <div ref={veil} className={LOOK.warm.veil} style={{ opacity: 0 }} />
      <div ref={top} data-testid="eyelid-top" className={`absolute origin-top ${LOOK.warm.lid}`} style={{ ...LID.top, transform: "scaleY(0)" }} />
      <div ref={bottom} data-testid="eyelid-bottom" className={`absolute origin-bottom ${LOOK.warm.lid}`} style={{ ...LID.bottom, transform: "scaleY(0)" }} />
    </div>
  );
}

// An observer's eye, painted, that softly opens, blinks twice and fades as the street comes in.
export function ArcEye({ arc, clock }) {
  const t = useArcTime(clock, false);
  const { from, to, blinks } = arc.eye;
  const open = Math.min(1, Math.max(0, (t - from) / 1.2)) * (1 - Math.max(0, Math.min(1, (t - to + 1) / 1)));
  const blink = blinks.some((b) => Math.abs(t - b) < 0.16) ? 0.08 : 1;
  const fade = Math.max(0, Math.min(1, (to - t) / 1.2));
  return (
    <div data-testid="arc-eye" data-open={open.toFixed(2)} className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{ opacity: t < to ? fade : 0 }}>
      <img
        src={`${process.env.PUBLIC_URL || ""}${arc.eye.src}`}
        alt=""
        className="w-[78vw] max-w-[560px] transition-[clip-path] duration-150 ease-out"
        style={{ clipPath: `ellipse(48% ${Math.max(1, 46 * open * blink)}% at 50% 50%)` }}
      />
    </div>
  );
}

// A plant's name in soft handwriting, with one tiny note, like a grandfather's lesson in the garden.
export function ArcNote() {
  const [note, setNote] = useState(null);
  useEffect(() => {
    let id;
    const on = (e) => {
      setNote({ ...e.detail, k: Date.now() });
      clearTimeout(id);
      id = setTimeout(() => setNote(null), 4200);
    };
    window.addEventListener("afae-note", on);
    return () => {
      window.removeEventListener("afae-note", on);
      clearTimeout(id);
    };
  }, []);
  return (
    <div data-testid="arc-note" className="pointer-events-none absolute inset-x-6 top-[40%] mx-auto max-w-md text-center sm:top-[30%]">
      <AnimatePresence mode="wait">
        {note && (
          <motion.div key={note.k} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
            <p data-testid="arc-note-name" className="font-hand text-3xl text-white drop-shadow-[0_2px_10px_rgba(30,20,40,0.7)]">{note.name}</p>
            <p data-testid="arc-note-text" className="mt-1 font-hand text-lg text-white/95 drop-shadow-[0_1px_8px_rgba(30,20,40,0.7)]">{note.note}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// One sun, a thousand names: the word for "sun" in many languages and scripts, while the sun itself never changes.
const SUN_NAMES = ["Sun", "सूर्य", "সূর্য", "Soleil", "Sol", "شمس", "太陽", "Sonne", "Ήλιος", "Солнце", "태양", "சூரியன்", "ਸੂਰਜ", "Jua", "Sun"];
const SCRIPT_FONTS = "https://fonts.googleapis.com/css2?family=Noto+Serif&family=Noto+Serif+Devanagari&family=Noto+Serif+Bengali&family=Noto+Naskh+Arabic&family=Noto+Serif+JP&family=Noto+Serif+KR&family=Noto+Serif+Tamil&family=Noto+Serif+Gurmukhi&display=swap";
const SCRIPT_STACK = "'Noto Serif','Noto Serif Devanagari','Noto Serif Bengali','Noto Naskh Arabic','Noto Serif JP','Noto Serif KR','Noto Serif Tamil','Noto Serif Gurmukhi',serif";
export function ArcSunNames({ arc, clock }) {
  const t = useArcTime(clock, false);
  useEffect(() => {
    if (document.querySelector(`link[href="${SCRIPT_FONTS}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = SCRIPT_FONTS;
    document.head.appendChild(l);
  }, []);
  const { from, to, end } = arc.names;
  const i = Math.max(0, Math.min(SUN_NAMES.length - 1, Math.floor(((t - from) / (to - from)) * SUN_NAMES.length)));
  const on = t >= from && t < end;
  return (
    <div data-testid="arc-sun-names" data-index={on ? i : -1} className="pointer-events-none absolute inset-x-0 top-[37%] flex justify-center sm:top-[30%]">
      {/* every script is set (invisibly) from the start, so its font is ready before its turn */}
      <span aria-hidden className="absolute whitespace-nowrap opacity-0" style={{ fontFamily: SCRIPT_STACK }}>{SUN_NAMES.join(" ")}</span>
      <AnimatePresence mode="wait">
        {on && (
          <motion.p
            key={i}
            data-testid="arc-sun-name"
            initial={{ opacity: 0, filter: "blur(6px)", scale: 0.96 }}
            animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
            exit={{ opacity: 0, filter: "blur(6px)", scale: 1.03 }}
            transition={{ duration: 0.2 }}
            className="text-4xl text-[#FFF1C9] [text-shadow:0_0_24px_rgba(255,200,90,0.8)] sm:text-5xl"
            style={{ fontFamily: SCRIPT_STACK }}
          >
            {SUN_NAMES[i]}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

// Words that belong to one moment of the journey, held quietly in the middle of the view.
export function ArcMoments({ arc, clock }) {
  const t = useArcTime(clock, false);
  const i = arc.moments.findIndex((m) => t >= m.from && t < m.to);
  return (
    <div data-testid="arc-moments" data-index={i} className="pointer-events-none absolute inset-x-6 top-[40%] flex justify-center sm:top-[34%]">
      <AnimatePresence mode="wait">
        {i >= 0 && (
          <motion.p key={i} data-testid="arc-moment" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 1.6, ease: "easeOut" }} className="rounded-2xl bg-[#22152F]/[0.7] px-5 py-2 text-center font-display text-3xl italic text-[#FFF1DE] backdrop-blur-sm sm:text-4xl">
            {arc.moments[i].text}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
